import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import CheckBox from 'components/final-form/CheckBox';
import { DropdownOption, mapToDropdownOptionArray } from 'components/final-form/dropdownUtils';
import Input from 'components/final-form/Input';
import Select from 'components/final-form/Select';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import PraxFormLabelSpan from 'components/layout/PraxFormLabelSpan';
import PraxFormScrollableContainer from 'components/layout/PraxFormScrollableContainer';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import MedicamentDosageSegment from 'components/medicaments/segments/MedicamentDosageSegment';
import MedicamentPriceSegment from 'components/medicaments/segments/MedicamentPriceSegment';
import arrayMutators from 'final-form-arrays';
import moment from 'moment';
import React, { Component } from "react";
import { Field, FieldProps, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, Header } from 'semantic-ui-react';
import { getAllDosages } from 'services/dosageServices';
import { addMedicament, editMedicament } from 'services/medicamentServices';
import { getAllVats } from 'services/vatGroupServices';
import styled from 'styled-components';
import { InsuranceAcceptance, StorageType } from 'ts-types/api.enums';
import { DosageDto, MedicamentDosage, MedicamentPrice, UpsertMedicamentDto, VatDto } from 'ts-types/api.types';
import { numberFormatter } from 'utils/fomattingUtils';
import { noop } from 'utils/functionUtils';
import { IndexSignature } from 'utils/praxTypes';
import { isMedicineTariffCode } from 'utils/tariffUtils';
import { composeValidators, maxChars, required } from 'utils/validatorUtils';

const StyledHeaderRow = styled(Grid.Row)`
  margin-top: 1rem !important;
`;

const StyledBorderColumn = styled(Grid.Column)`
  border-bottom: 1px solid rgba(34, 36, 38, .1);
`;

interface Props extends AuthConsumerRenderProps, MessageSourceProps {
  toggleAddEditModal: () => void,
  onCancel: () => void,
  onSave: () => void,
  medicamentId?: number,
  medicament?: UpsertMedicamentDto
  tariffCode?: string
}

interface State {
  medicament?: Partial<UpsertMedicamentDto>,
  errorMessage?: string,
  vats?: Array<DropdownOption>,
  storageTypes?: Array<DropdownOption>,
  insuranceAcceptance?: Array<DropdownOption>,
  dosage?: Array<DosageDto>
  medicamentPrices: Array<MedicamentPrice>
  oldPrice?: number,
  medicamentDosages: Array<MedicamentDosage>,
  cancelTokenSource: CancelTokenSource
}


const VALIDATED_FIELDS: Array<string> = [
  "code", "description"
];

class UpsertMedicamentForm extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const {getMessage, medicament} = this.props;
    const cancelTokenSource = axios.CancelToken.source();

    const insuranceAcceptance: Array<DropdownOption> = Object.values(InsuranceAcceptance).map((value: string) => {
      return {
        key: value,
        text: getMessage(`ia.${value.replace(new RegExp("_", "g"), "").toLowerCase()}`),
        value: value
      }
    });

    const storageTypes: Array<DropdownOption> = Object.values(StorageType).map((value: string) => {
      return {
        key: value,
        text: getMessage(`st.${value.replace(new RegExp("_", "g"), "").toLowerCase()}`),
        value: value
      }
    });

    this.state = {
      medicament: medicament ? medicament : this.initialValues(),
      vats: [],
      storageTypes: storageTypes,
      insuranceAcceptance: insuranceAcceptance,
      dosage: [],
      medicamentPrices: medicament ? medicament.medicamentPrices : [],
      oldPrice: medicament ? medicament.oldPrice : undefined,
      medicamentDosages: medicament ? medicament.medicamentDosages : [],
      cancelTokenSource
    };

    Promise.all([getAllVats(cancelTokenSource), getAllDosages(cancelTokenSource)])
        .then(responses => {

          let vats: Array<VatDto>;
          if (medicament) {
            vats = responses[0].filter(vat => vat.id === medicament.vatId || !vat.deleted)
          } else {
            vats = responses[0].filter(vat => !vat.deleted)
          }

          this.setState({
            vats: mapToDropdownOptionArray(vats, "description", "id"),
            dosage: responses[1]
          })
        }).catch(noop);
  };

  componentWillUnmount(): void {

    const {cancelTokenSource} = this.state;
    cancelTokenSource.cancel('Operation canceled by user');
  }

  initialValues = (): Partial<UpsertMedicamentDto> => {

    const {
      tariffCode,
      options: {workDate}
    } = this.props;

    return {
      code: "",
      description: "",
      selection: true,
      storageType: StorageType.NO_LIMIT,
      vatId: 1,
      tariffCode: tariffCode,
      medicamentPrices: [],
      insuranceAcceptance: InsuranceAcceptance.SPECIALITY_LIST,
      effectiveDate: workDate,
      type: isMedicineTariffCode(tariffCode!) ? "0" : "1",
      shippable: false
    };
  }

  getMedicamentDosageFromChildSegment = (medicamentDosage: Array<MedicamentDosage>) => {
    this.setState({
      medicamentDosages: medicamentDosage
    })
  };

  getMedicamentPricesFromChildSegment = (medicamentPrices: Array<MedicamentPrice>) => {
    const {medicament} = this.state;
    this.setState({
      medicamentPrices: medicamentPrices,
      oldPrice: medicamentPrices.length > 1 ? medicamentPrices[1].billingPrice : medicament!.oldPrice
    })
  };

  handleSubmit = async (values: Partial<UpsertMedicamentDto>) => {
    const {medicamentDosages, medicamentPrices, oldPrice} = this.state;
    const {medicamentId, getMessage, onSave} = this.props;

    let upsertMedicamentDto = {...values};

    if (medicamentPrices.length > 0) {
      const currentPrice =
          medicamentPrices.reduce((a, b) => (a.fromDate > b.fromDate ? a : b));

      upsertMedicamentDto.effectiveDate = currentPrice.fromDate;
      upsertMedicamentDto.billingPrice = currentPrice.billingPrice;
      upsertMedicamentDto.doctorsPrice = currentPrice.doctorsPrice;
      upsertMedicamentDto.unitQty = currentPrice.unitQty;
    }

    const updatedValues: Partial<UpsertMedicamentDto> = {
      ...upsertMedicamentDto,
      oldPrice,
      medicamentPrices,
      medicamentDosages
    };

    const {cancelTokenSource} = this.state;

    const handleError = () => {
      this.setState({
        errorMessage: getMessage("error.general")
      })
    };

    if (medicamentId) {
      await editMedicament(medicamentId, updatedValues, cancelTokenSource)
          .then(onSave)
          .catch(handleError)
    } else {
      await addMedicament(updatedValues, cancelTokenSource)
          .then(onSave)
          .catch(handleError)
    }
  };

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  render(): React.ReactNode {

    const {medicament} = this.state;

    return (
        <FinalForm
            onSubmit={(values) => this.handleSubmit(values)}
            initialValues={medicament}
            mutators={{
              ...arrayMutators
            }}
            subscription={{pristine: true, values: true, submitting: true}}
            render={this.renderForm}
        />
    );
  }

  renderForm = ({handleSubmit, values, submitting}: FormRenderProps): React.ReactNode => {

    const {getMessage, onCancel} = this.props;
    const {errorMessage} = this.state;

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
              <PraxFormGrid>
                <PraxFormScrollableContainer height="690px">
                  {errorMessage
                      && (
                          <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                            {errorMessage}
                          </StyledErrorMessage>
                      )
                  }
                  <PraxFormGrid>
                    {this.renderBasicInformationSection(values)}
                  </PraxFormGrid>
                </PraxFormScrollableContainer>
              </PraxFormGrid>
            </FormContent>

            <FormButtonsRow>
              <Button compact type="submit" primary disabled={submitting}>{getMessage("button.save")}</Button>
              <Button compact type="button" disabled={submitting}
                      onClick={onCancel}>{getMessage("button.cancel")}</Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    );
  };

  renderPriceInformation = (values: Partial<UpsertMedicamentDto>): React.ReactNode => {

    const {getMessage} = this.props;
    const {oldPrice, medicamentPrices} = this.state;

    return (

        <React.Fragment>
          <Grid.Row>
            <Grid.Column width={8}>
              {getMessage("billingPrice")}:
            </Grid.Column>
            <Grid.Column width={8}>
              {medicamentPrices.length > 0
                  ? numberFormatter.format(medicamentPrices[0].billingPrice)
                  : values.billingPrice && numberFormatter.format(values.billingPrice)}
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={8}>
              {getMessage("from")} {getMessage("date")}:
            </Grid.Column>
            <Grid.Column width={8}>
              {medicamentPrices.length > 0
                  ? moment(medicamentPrices[0].fromDate).format("DD.MM.YYYY")
                  : moment(values.effectiveDate).format("DD.MM.YYYY")
              }
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={8}>
              {getMessage("oldPrice")}:
            </Grid.Column>
            <Grid.Column width={8}>
              {oldPrice
                  ? numberFormatter.format(oldPrice)
                  : values.oldPrice && numberFormatter.format(values.oldPrice)}
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={8}>
              {getMessage("doctorsPrice")}:
            </Grid.Column>
            <Grid.Column width={8}>
              {medicamentPrices.length > 0
                  ? numberFormatter.format(medicamentPrices[0].doctorsPrice)
                  : values.doctorsPrice && numberFormatter.format(values.doctorsPrice)}
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={8}>
              {getMessage("unitQty")}:
            </Grid.Column>
            <Grid.Column width={8}>
              {medicamentPrices.length > 0
                  ? numberFormatter.format(medicamentPrices[0].unitQty)
                  : values.unitQty && numberFormatter.format(values.unitQty)}
            </Grid.Column>
          </Grid.Row>
        </React.Fragment>

    );

  };

  renderBasicInformationSection = (values: Partial<UpsertMedicamentDto>): React.ReactNode => {

    const {vats, storageTypes, insuranceAcceptance, dosage, medicamentDosages, medicamentPrices} = this.state;
    const {getMessage} = this.props;

    const selectMap: IndexSignature = {
      vatId: vats,
      storageType: storageTypes,
      insuranceAcceptance: insuranceAcceptance
    };

    const addressRows: Array<Array<string>> = [
      ["code"],
      ["description"],
      ["externalOrderNumber"]
    ];

    const additionalRows: Array<Array<string>> = [
      ["pharmaCode"],
      ["eanCode"],
      ["thIdx"],
      ["vatId"],
      ["storageType"],
      ["insuranceAcceptance"]
    ];

    const validator = (value: string) => required(value);
    const codeValidator = composeValidators(maxChars(50), required);

    const descriptionSection = addressRows.map((row, rowIx) => {
      return (
          <Grid.Row key={rowIx}>
            {
              row.map((field, fieldIx) => {
                let fieldProps: FieldProps = {
                  name: field,
                  validate: VALIDATED_FIELDS.includes(field) ? field === "code" ? codeValidator : validator : undefined,
                  component: Input
                };

                return (
                    <React.Fragment key={fieldIx}>
                      <Grid.Column width={5} verticalAlign="middle">
                        <PraxFormLabelSpan htmlFor={field + fieldIx}>{`${getMessage(field)}:`}</PraxFormLabelSpan>
                      </Grid.Column>
                      <Grid.Column width={fieldProps.name === "code" ? 5 : 11} verticalAlign="middle">
                        <Field
                            fluid
                            id={field + fieldIx}
                            {...fieldProps}
                        />
                      </Grid.Column>
                      {fieldProps.name === "code" &&
                          <Grid.Column width={6}>
                            <Field
                                name="shippable"
                                component={CheckBox}
                                label={getMessage("medicament.shippable")}
                            />
                          </Grid.Column>
                      }
                    </React.Fragment>
                );
              })
            }
          </Grid.Row>
      );
    });

    const detailInformation = additionalRows.map((row, rowIx) => {
      return (
          <Grid.Row key={rowIx}>
            {
              row.map((field, fieldIx) => {
                let fieldProps: FieldProps = {
                  name: field,
                  validate: VALIDATED_FIELDS.includes(field) ? validator : undefined,
                  component: Input
                };

                if (selectMap[field]) {
                  fieldProps = {
                    ...fieldProps,
                    component: Select,
                    search: true,
                    options: selectMap[field],
                    upward: true
                  }
                }

                if (field === "eanCode" || field === "pharmaCode") {

                  const maxNumOfChars: number = field === "eanCode" ? 14 : 8;

                  fieldProps = {
                    name: field,
                    validate: maxChars(maxNumOfChars),
                    component: Input
                  }
                }

                return (
                    <React.Fragment key={fieldIx}>
                      <Grid.Column width={5} verticalAlign="middle">
                        <PraxFormLabelSpan htmlFor={field + fieldIx}>{`${getMessage(field)}:`}</PraxFormLabelSpan>
                      </Grid.Column>
                      <Grid.Column width={11} verticalAlign="middle">
                        <Field
                            fluid
                            id={field + fieldIx}
                            {...fieldProps}
                        />
                      </Grid.Column>
                    </React.Fragment>
                );
              })
            }
          </Grid.Row>
      );
    });

    return (
        <PraxFormGrid>
          <Grid.Row>
            <Grid.Column>
              <PraxFormGrid>
                <Grid.Row>
                  <StyledBorderColumn>
                    <Header as="h5">
                      {getMessage("description")}
                    </Header>
                  </StyledBorderColumn>
                </Grid.Row>
                {descriptionSection}
              </PraxFormGrid>
            </Grid.Column>
          </Grid.Row>
          <StyledHeaderRow>
            <StyledBorderColumn>
              <Header as="h5">
                {getMessage("medicament.price.information")}
              </Header>
            </StyledBorderColumn>
          </StyledHeaderRow>
          <Grid.Row>
            <Grid.Column width={5}>
              <PraxFormGrid>
                <Grid.Row>
                </Grid.Row>
                {this.renderPriceInformation(values)}
              </PraxFormGrid>
            </Grid.Column>
            <MedicamentPriceSegment
                medicamentPrice={medicamentPrices}
                getMedicamentPrices={this.getMedicamentPricesFromChildSegment}
            />
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={7}>
              <PraxFormGrid>
                <StyledHeaderRow>
                  <StyledBorderColumn>
                    <Header as="h5">
                      {getMessage("medicament.detail.information")}
                    </Header>
                  </StyledBorderColumn>
                </StyledHeaderRow>
                {detailInformation}
              </PraxFormGrid>
            </Grid.Column>
            <Grid.Column width={9}>
              {dosage && dosage.length > 0 ?
                  (<MedicamentDosageSegment
                      dosage={dosage}
                      medicamentDosage={medicamentDosages && medicamentDosages.length > 0 ? medicamentDosages : []}
                      passMedDosage={this.getMedicamentDosageFromChildSegment}
                  />) : ""}
            </Grid.Column>
          </Grid.Row>
        </PraxFormGrid>
    );
  };

}

export default withAuthContext(withMessages(UpsertMedicamentForm));
